import React from "react";
import { Input, Field, Switch } from "@fluentui/react-components";
import { DialogBox } from "#root/components/DialogBox";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { TENANT_TYPE } from "#root/utils/tenants";
import { useCurrentTenant } from "#root/queries/local";
import { useTenantById } from "#root/queries/tenants";
import { formatToLocalTime, createLocalDate } from "#root/services/utils";

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

interface CreateReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (reportData: {
    reportName: string;
    startDate: Date;
    endDate: Date;
    improvementPlanTable: boolean;
  }) => Promise<void>;
  defaultReportName?: string;
}


const formatLocalDate = (d: Date | undefined | null) =>
  d ? formatToLocalTime(d, 'date', false) : "";

const parseDateFromString = (text: string): Date | null => {
  const p = dayjs(text, "MM/DD/YYYY", true);
  if (!p.isValid()) return null;
  return p.toDate();
};

const toLocalMidnight = (localPicked: Date): Date => {
  const date = new Date(localPicked);
  date.setHours(0, 0, 0, 0);
  return date;
};

const toLocalEndOfDay = (localPicked: Date): Date => {
  const date = new Date(localPicked);
  date.setHours(23, 59, 59, 999);
  return date;
};

export const CreateReportDialog: React.FC<CreateReportDialogProps> = ({
  open,
  onOpenChange,
  onCreate,
  defaultReportName = "",
}) => {
  const [reportName, setReportName] = React.useState(defaultReportName);
  const [startDate, setStartDate] = React.useState<Date | null>(null);
  const [endDate, setEndDate] = React.useState<Date | null>(null);
  const [improvementPlanTable, setImprovementPlan] = React.useState(false);
  const [isPrimaryButtonLoading, setPrimaryButtonLoading] = React.useState(false);
  const { data: currentTenantId } = useCurrentTenant();
  const { data: tenantResponse } = useTenantById({ id: currentTenantId || '' });
  const currentTenant = tenantResponse?.item;
  const tenantType = currentTenant?.type || TENANT_TYPE.RAPID_REUNIFICATION;
  const showImprovementPlanField = tenantType !== TENANT_TYPE.MERRGE_211;
  React.useEffect(() => {
    if (open) {
      setReportName(defaultReportName);
      setStartDate(null);
      setEndDate(null);
      setImprovementPlan(false);
    }
  }, [open, defaultReportName]);

  React.useEffect(() => {
    if (endDate && startDate && endDate < startDate) {
      setEndDate(startDate);
    }
  }, [startDate, endDate]);

  const handleCreate = async () => {
    if (!startDate || !endDate) return;
    setPrimaryButtonLoading(true);
    try {
      await onCreate({
        reportName: reportName.trim(),
        startDate,
        endDate,
        improvementPlanTable,
      });
      onOpenChange(false);
    } finally {
      setPrimaryButtonLoading(false);
    }
  };

  const disabled = !reportName.trim() || !startDate || !endDate;

  return (
    <DialogBox
      open={open}
      onOpenChange={onOpenChange}
      title="New Report"
      primaryButtonText="Create"
      primaryButtonAppearance="primary"
      onPrimaryButtonClick={handleCreate}
      isPrimaryButtonDisabled={disabled}
      isPrimaryButtonLoading={isPrimaryButtonLoading}
      showSecondaryButton
      secondaryButtonText="Cancel"
      onSecondaryButtonClick={() => onOpenChange(false)}
    >
      <form>
        <Field label="Name of the Report" required>
          <Input
            value={reportName}
            onChange={(e, data) => setReportName(data.value)}
            placeholder="Enter the report name"
          />
        </Field>

        <div className="field-two-columns">
          <Field label="Start Date" required>
            <DatePicker
              value={startDate}
              onSelectDate={(d) => setStartDate(d ? toLocalMidnight(d) : null)}
              formatDate={formatLocalDate}
              parseDateFromString={parseDateFromString}
              placeholder="MM/DD/YYYY"
              allowTextInput
            />
          </Field>

          <Field label="End Date" required>
            <DatePicker
              value={endDate}
              minDate={startDate ?? undefined}
              onSelectDate={(d) => setEndDate(d ? toLocalEndOfDay(d) : null)}
              formatDate={formatLocalDate}
              parseDateFromString={parseDateFromString}
              placeholder="MM/DD/YYYY"
              allowTextInput
            />
          </Field>
        </div>
        {showImprovementPlanField && (
          <Field label="Improvement Plan Table">
            <Switch
              checked={improvementPlanTable}
              onChange={(_, data) => setImprovementPlan(data.checked)}
            />
          </Field>
        )}
      </form>
    </DialogBox>
  );
};
